import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  createCourse,
  updateCourse,
  getCourseById,
  createModule,
  updateModule,
  deleteModule,
  reorderModules,
  createLesson,
  updateLesson,
  deleteLesson,
  reorderLessons,
} from '../../api/educentialApi';
import { ChevronDown, ChevronRight, Edit2, Trash2 } from 'lucide-react';
import './CourseBuilder.css';

function CourseBuilder() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Course data
  const [course, setCourse] = useState({
    title: '',
    description: '',
    category: 'Mathematics',
    level: 'JSS1',
    term: 'First Term',
    duration: 10,
    thumbnail: '',
    price: 0,
  });

  const [modules, setModules] = useState([]);
  const [activeTab, setActiveTab] = useState('details'); // details, content
  const [expandedModule, setExpandedModule] = useState(null);

  // Modal states
  const [showModuleModal, setShowModuleModal] = useState(false);
  const [showLessonModal, setShowLessonModal] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  const [editingLesson, setEditingLesson] = useState(null);
  const [currentModuleId, setCurrentModuleId] = useState(null);

  useEffect(() => {
    if (isEditing) {
      loadCourse();
    }
  }, [id]);

  const loadCourse = async () => {
    try {
      setLoading(true);
      const response = await getCourseById(id);
      if (response.success) {
        const courseData = response.data;
        setCourse({
          title: courseData.title,
          description: courseData.description,
          category: courseData.category,
          level: courseData.level,
          term: courseData.term || '',
          duration: courseData.duration,
          thumbnail: courseData.thumbnail || '',
          price: courseData.price ? parseFloat(courseData.price) : 0,
        });
        setModules(courseData.Modules || []);
      }
    } catch (err) {
      alert('Failed to load course: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCourse = async (publish = false) => {
    if (!course.title || !course.description) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      const courseData = {
        ...course,
        duration: parseInt(course.duration),
        price: parseFloat(course.price) || null,
      };

      let response;
      if (isEditing) {
        response = await updateCourse(id, courseData);
      } else {
        response = await createCourse(courseData);
      }

      if (response.success) {
        alert(isEditing ? 'Course updated!' : 'Course created!');
        if (!isEditing) {
          navigate(`/educator/courses/${response.data.id}/edit`);
        }
      }
    } catch (err) {
      alert('Failed to save course: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAddModule = async (moduleData) => {
    try {
      const response = await createModule(id, {
        ...moduleData,
        order: modules.length + 1,
      });
      if (response.success) {
        setModules([...modules, response.data]);
        setShowModuleModal(false);
      }
    } catch (err) {
      alert('Failed to add module: ' + err.message);
    }
  };

  const handleUpdateModule = async (moduleId, moduleData) => {
    try {
      const response = await updateModule(id, moduleId, moduleData);
      if (response.success) {
        setModules(modules.map((m) => (m.id === moduleId ? response.data : m)));
        setShowModuleModal(false);
        setEditingModule(null);
      }
    } catch (err) {
      alert('Failed to update module: ' + err.message);
    }
  };

  const handleDeleteModule = async (moduleId) => {
    if (!window.confirm('Delete this module and all its lessons?')) return;

    try {
      await deleteModule(id, moduleId);
      setModules(modules.filter((m) => m.id !== moduleId));
    } catch (err) {
      alert('Failed to delete module: ' + err.message);
    }
  };

  const handleAddLesson = async (lessonData) => {
    try {
      const module = modules.find((m) => m.id === currentModuleId);
      const response = await createLesson(currentModuleId, {
        ...lessonData,
        order: (module.Lessons?.length || 0) + 1,
      });
      if (response.success) {
        setModules(
          modules.map((m) =>
            m.id === currentModuleId
              ? { ...m, Lessons: [...(m.Lessons || []), response.data] }
              : m
          )
        );
        setShowLessonModal(false);
        setCurrentModuleId(null);
      }
    } catch (err) {
      alert('Failed to add lesson: ' + err.message);
    }
  };

  const handleUpdateLesson = async (lessonId, lessonData) => {
    try {
      const response = await updateLesson(currentModuleId, lessonId, lessonData);
      if (response.success) {
        setModules(
          modules.map((m) =>
            m.id === currentModuleId
              ? {
                  ...m,
                  Lessons: m.Lessons.map((l) => (l.id === lessonId ? response.data : l)),
                }
              : m
          )
        );
        setShowLessonModal(false);
        setEditingLesson(null);
        setCurrentModuleId(null);
      }
    } catch (err) {
      alert('Failed to update lesson: ' + err.message);
    }
  };

  const handleDeleteLesson = async (moduleId, lessonId) => {
    if (!window.confirm('Delete this lesson?')) return;

    try {
      await deleteLesson(moduleId, lessonId);
      setModules(
        modules.map((m) =>
          m.id === moduleId ? { ...m, Lessons: m.Lessons.filter((l) => l.id !== lessonId) } : m
        )
      );
    } catch (err) {
      alert('Failed to delete lesson: ' + err.message);
    }
  };

  if (loading) {
    return <div className="course-builder-container loading">Loading course...</div>;
  }

  return (
    <div className="course-builder-container">
      <div className="builder-header">
        <div>
          <h1>{isEditing ? 'Edit Course' : 'Create New Course'}</h1>
          <p className="builder-subtitle">Build engaging courses for your students</p>
        </div>
        <div className="header-actions">
          <button className="btn-secondary" onClick={() => navigate('/educator/courses')}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={() => handleSaveCourse()}
            disabled={saving}
          >
            {saving ? 'Saving...' : isEditing ? 'Update Course' : 'Create Course'}
          </button>
        </div>
      </div>

      <div className="builder-tabs">
        <button
          className={activeTab === 'details' ? 'active' : ''}
          onClick={() => setActiveTab('details')}
        >
          Course Details
        </button>
        <button
          className={activeTab === 'content' ? 'active' : ''}
          onClick={() => setActiveTab('content')}
          disabled={!isEditing}
        >
          Content & Modules
        </button>
      </div>

      {activeTab === 'details' && (
        <div className="builder-section">
          <div className="form-grid">
            <div className="form-group full-width">
              <label>Course Title *</label>
              <input
                type="text"
                value={course.title}
                onChange={(e) => setCourse({ ...course, title: e.target.value })}
                placeholder="e.g., Mathematics for JSS1 - First Term"
              />
            </div>

            <div className="form-group full-width">
              <label>Description *</label>
              <textarea
                value={course.description}
                onChange={(e) => setCourse({ ...course, description: e.target.value })}
                placeholder="Describe what students will learn in this course..."
                rows="4"
              />
            </div>

            <div className="form-group">
              <label>Category *</label>
              <select
                value={course.category}
                onChange={(e) => setCourse({ ...course, category: e.target.value })}
              >
                <option value="Mathematics">Mathematics</option>
                <option value="English">English Language</option>
                <option value="Science">Science</option>
                <option value="Social Studies">Social Studies</option>
                <option value="Arts">Creative Arts</option>
                <option value="Computer Science">Computer Science</option>
                <option value="Physical Education">Physical Education</option>
              </select>
            </div>

            <div className="form-group">
              <label>Level *</label>
              <select
                value={course.level}
                onChange={(e) => setCourse({ ...course, level: e.target.value })}
              >
                <option value="Primary">Primary</option>
                <option value="JSS1">JSS1</option>
                <option value="JSS2">JSS2</option>
                <option value="JSS3">JSS3</option>
                <option value="SS1">SS1</option>
                <option value="SS2">SS2</option>
                <option value="SS3">SS3</option>
              </select>
            </div>

            <div className="form-group">
              <label>Term</label>
              <select
                value={course.term}
                onChange={(e) => setCourse({ ...course, term: e.target.value })}
              >
                <option value="">Not Specified</option>
                <option value="First Term">First Term</option>
                <option value="Second Term">Second Term</option>
                <option value="Third Term">Third Term</option>
              </select>
            </div>

            <div className="form-group">
              <label>Duration (hours)</label>
              <input
                type="number"
                value={course.duration}
                onChange={(e) => setCourse({ ...course, duration: e.target.value })}
                min="1"
              />
            </div>

            <div className="form-group full-width">
              <label>Thumbnail URL</label>
              <input
                type="text"
                value={course.thumbnail}
                onChange={(e) => setCourse({ ...course, thumbnail: e.target.value })}
                placeholder="https://example.com/image.jpg"
              />
              {course.thumbnail && (
                <img src={course.thumbnail} alt="Preview" className="thumbnail-preview" />
              )}
            </div>

            <div className="form-group">
              <label>Price (₦)</label>
              <input
                type="number"
                value={course.price}
                onChange={(e) => setCourse({ ...course, price: e.target.value })}
                min="0"
                step="0.01"
                placeholder="0 for free"
              />
            </div>
          </div>
        </div>
      )}

      {activeTab === 'content' && (
        <div className="builder-section">
          <div className="content-header">
            <h2>Course Modules & Lessons</h2>
            <button
              className="btn-primary"
              onClick={() => {
                setEditingModule(null);
                setShowModuleModal(true);
              }}
            >
              + Add Module
            </button>
          </div>

          {modules.length === 0 ? (
            <div className="empty-content">
              <p>No modules yet. Add your first module to start building course content.</p>
            </div>
          ) : (
            <div className="modules-list">
              {modules.map((module, index) => (
                <ModuleItem
                  key={module.id}
                  module={module}
                  index={index}
                  expanded={expandedModule === module.id}
                  onToggle={() =>
                    setExpandedModule(expandedModule === module.id ? null : module.id)
                  }
                  onEdit={() => {
                    setEditingModule(module);
                    setShowModuleModal(true);
                  }}
                  onDelete={() => handleDeleteModule(module.id)}
                  onAddLesson={() => {
                    setCurrentModuleId(module.id);
                    setEditingLesson(null);
                    setShowLessonModal(true);
                  }}
                  onEditLesson={(lesson) => {
                    setCurrentModuleId(module.id);
                    setEditingLesson(lesson);
                    setShowLessonModal(true);
                  }}
                  onDeleteLesson={(lessonId) => handleDeleteLesson(module.id, lessonId)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {showModuleModal && (
        <ModuleModal
          module={editingModule}
          onSave={(data) =>
            editingModule
              ? handleUpdateModule(editingModule.id, data)
              : handleAddModule(data)
          }
          onClose={() => {
            setShowModuleModal(false);
            setEditingModule(null);
          }}
        />
      )}

      {showLessonModal && (
        <LessonModal
          lesson={editingLesson}
          onSave={(data) =>
            editingLesson
              ? handleUpdateLesson(editingLesson.id, data)
              : handleAddLesson(data)
          }
          onClose={() => {
            setShowLessonModal(false);
            setEditingLesson(null);
            setCurrentModuleId(null);
          }}
        />
      )}
    </div>
  );
}

// Module Item Component
function ModuleItem({
  module,
  index,
  expanded,
  onToggle,
  onEdit,
  onDelete,
  onAddLesson,
  onEditLesson,
  onDeleteLesson,
}) {
  return (
    <div className="module-item">
      <div className="module-header" onClick={onToggle}>
        <div className="module-info">
          <span className="module-number">Module {index + 1}</span>
          <h3>{module.title}</h3>
          <span className="lesson-count">{module.Lessons?.length || 0} lessons</span>
        </div>
        <div className="module-actions" onClick={(e) => e.stopPropagation()}>
          <button className="btn-icon" onClick={onEdit}>
            <Edit2 className="w-4 h-4" />
          </button>
          <button className="btn-icon" onClick={onDelete}>
            <Trash2 className="w-4 h-4" />
          </button>
          <span className="expand-icon">
            {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </span>
        </div>
      </div>

      {expanded && (
        <div className="module-content">
          {module.description && <p className="module-description">{module.description}</p>}

          <div className="lessons-section">
            <button className="btn-add-lesson" onClick={onAddLesson}>
              + Add Lesson
            </button>

            {module.Lessons && module.Lessons.length > 0 && (
              <div className="lessons-list">
                {module.Lessons.map((lesson, lessonIndex) => (
                  <div key={lesson.id} className="lesson-item">
                    <div className="lesson-info">
                      <span className="lesson-number">{lessonIndex + 1}</span>
                      <div className="lesson-details">
                        <h4>{lesson.title}</h4>
                        <div className="lesson-meta">
                          <span className={`lesson-type ${lesson.type.toLowerCase()}`}>
                            {lesson.type}
                          </span>
                          {lesson.duration && <span>{lesson.duration} min</span>}
                        </div>
                      </div>
                    </div>
                    <div className="lesson-actions">
                      <button className="btn-icon" onClick={() => onEditLesson(lesson)}>
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button className="btn-icon" onClick={() => onDeleteLesson(lesson.id)}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Module Modal Component
function ModuleModal({ module, onSave, onClose }) {
  const [formData, setFormData] = useState({
    title: module?.title || '',
    description: module?.description || '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.title) {
      alert('Please enter a module title');
      return;
    }
    onSave(formData);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>{module ? 'Edit Module' : 'Add Module'}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Module Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., Introduction to Algebra"
              autoFocus
            />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description of this module..."
              rows="3"
            />
          </div>
          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              {module ? 'Update' : 'Add'} Module
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Lesson Modal Component
function LessonModal({ lesson, onSave, onClose }) {
  const [formData, setFormData] = useState({
    title: lesson?.title || '',
    type: lesson?.type || 'ARTICLE',
    content: lesson?.content || '',
    videoUrl: lesson?.videoUrl || '',
    fileUrl: lesson?.fileUrl || '',
    duration: lesson?.duration || '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.title) {
      alert('Please enter a lesson title');
      return;
    }
    onSave({
      ...formData,
      duration: formData.duration ? parseInt(formData.duration) : null,
    });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
        <h2>{lesson ? 'Edit Lesson' : 'Add Lesson'}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Lesson Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., Understanding Variables"
              autoFocus
            />
          </div>

          <div className="form-group">
            <label>Lesson Type *</label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
            >
              <option value="ARTICLE">Article</option>
              <option value="VIDEO">Video</option>
              <option value="DOCUMENT">Document</option>
              <option value="QUIZ">Quiz</option>
              <option value="ASSIGNMENT">Assignment</option>
            </select>
          </div>

          <div className="form-group">
            <label>Duration (minutes)</label>
            <input
              type="number"
              value={formData.duration}
              onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
              placeholder="Optional"
              min="1"
            />
          </div>

          {formData.type === 'ARTICLE' && (
            <div className="form-group">
              <label>Content *</label>
              <ReactQuill
                value={formData.content}
                onChange={(value) => setFormData({ ...formData, content: value })}
                theme="snow"
                modules={{
                  toolbar: [
                    [{ header: [1, 2, 3, false] }],
                    ['bold', 'italic', 'underline', 'strike'],
                    [{ list: 'ordered' }, { list: 'bullet' }],
                    ['blockquote', 'code-block'],
                    ['link', 'image'],
                    ['clean'],
                  ],
                }}
              />
            </div>
          )}

          {formData.type === 'VIDEO' && (
            <div className="form-group">
              <label>Video URL *</label>
              <input
                type="text"
                value={formData.videoUrl}
                onChange={(e) => setFormData({ ...formData, videoUrl: e.target.value })}
                placeholder="https://youtube.com/watch?v=..."
              />
              <small>Supports YouTube, Vimeo, and direct video URLs</small>
            </div>
          )}

          {formData.type === 'DOCUMENT' && (
            <div className="form-group">
              <label>Document URL *</label>
              <input
                type="text"
                value={formData.fileUrl}
                onChange={(e) => setFormData({ ...formData, fileUrl: e.target.value })}
                placeholder="https://example.com/document.pdf"
              />
              <small>Upload PDF or document files</small>
            </div>
          )}

          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              {lesson ? 'Update' : 'Add'} Lesson
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CourseBuilder;
